import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenantLocation } from '../context/LocationContext';
import { api } from '../lib/api';
import { DEFAULT_CURRENCY } from '../lib/currency';
import { CurrencyPicker } from '../components/locations/CurrencyPicker';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  MapPin, Plus, Loader2, Star, Plug, Pencil, Archive, Trash2,
  CheckCircle2, AlertCircle, Cloud, Server, Wifi, Building2, Database,
  Copy, RefreshCw, FileUp, Webhook, Hand,
} from 'lucide-react';

const CLOUD_DEFAULT = 'https://api.untill.com/shield/api/v1';
const SELF_DEFAULT = 'https://untill.mycompany.local:8443/shield/api/v1';

const SoapBillFields = ({ form, setForm }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 space-y-3" data-testid="soap-bill-fields">
    <div>
      <div className="text-sm font-medium text-white">Closed bills via SOAP (TPAPI)</div>
      <p className="text-xs text-slate-400">Optional. unTill exposes bill history on its TPAPI SOAP service. Leave blank to keep REST-only probing.</p>
    </div>
    <div>
      <Label className="text-slate-300">SOAP URL</Label>
      <Input value={form.soap_url} placeholder="http://your-untill-host:3063/soap/ITPAPIPOS"
        onChange={(e) => setForm({ ...form, soap_url: e.target.value })}
        className="bg-slate-950 border-slate-800 text-white mt-1.5 font-mono text-sm"
        data-testid="location-soap-url" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label className="text-slate-300">FindTransactions "from" key <span className="text-slate-500 text-xs">(fallback)</span></Label>
        <Input value={form.soap_from_key} placeholder="from unTill support"
          onChange={(e) => setForm({ ...form, soap_from_key: e.target.value })}
          className="bg-slate-950 border-slate-800 text-white mt-1.5 font-mono text-sm"
          data-testid="location-soap-from-key" />
      </div>
      <div>
        <Label className="text-slate-300">FindTransactions "to" key <span className="text-slate-500 text-xs">(fallback)</span></Label>
        <Input value={form.soap_to_key} placeholder="from unTill support"
          onChange={(e) => setForm({ ...form, soap_to_key: e.target.value })}
          className="bg-slate-950 border-slate-800 text-white mt-1.5 font-mono text-sm"
          data-testid="location-soap-to-key" />
      </div>
    </div>
  </div>
);

const emptyForm = {
  location_name: '',
  currency: DEFAULT_CURRENCY,
  transport: 'rest',
  instance_type: 'cloud',
  base_url: CLOUD_DEFAULT,
  tenant_id: '',
  username: '',
  password: '',
  app_token: '',
  app_name: 'SyncPoint',
  site_label: '',
  soap_url: '',
  soap_from_key: '',
  soap_to_key: '',
};

const TRANSPORTS = [
  { id: 'rest', label: 'REST API', hint: 'Live pull from unTill Cloud or self-hosted' },
  { id: 'csv', label: 'CSV Upload', hint: 'Merchant drops daily .csv or .xlsx files' },
  { id: 'webhook', label: 'Webhook', hint: 'POS pushes sales to a signed URL' },
  { id: 'manual', label: 'Manual', hint: 'Small venues — enter data by hand' },
];

export default function Locations() {
  const { token } = useAuth();
  const { locations, refresh, currentLocationId, setCurrentLocationId } = useTenantLocation();
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // Add/Edit dialog
  const [dialog, setDialog] = useState({ open: false, mode: 'create', location: null });
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Destination profiles + per-location bindings
  const [destinations, setDestinations] = useState({ qbo_profiles: [], odoo_profiles: [], bindings: [] });
  const [bindingBusy, setBindingBusy] = useState(null);

  // Delete confirm dialog
  const [confirmDel, setConfirmDel] = useState(null); // location object or null
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      await refresh();
      try {
        const d = await api.listDestinations(token);
        setDestinations(d.data);
      } catch (e) { /* non-fatal */ }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const openCreate = () => {
    setForm(emptyForm);
    setDialog({ open: true, mode: 'create', location: null });
  };

  const openEdit = (loc) => {
    setForm({
      location_name: loc.location_name || '',
      currency: loc.currency || DEFAULT_CURRENCY,
      transport: loc.transport || 'rest',
      instance_type: loc.instance_type || 'cloud',
      base_url: loc.base_url || CLOUD_DEFAULT,
      tenant_id: loc.tenant_id || '',
      username: loc.username || '',
      password: '',
      app_token: '',
      app_name: loc.app_name || 'SyncPoint',
      site_label: loc.site_label || '',
      soap_url: loc.soap_url || '',
      soap_from_key: loc.soap_from_key || '',
      soap_to_key: loc.soap_to_key || '',
    });
    setDialog({ open: true, mode: 'edit', location: loc });
  };

  const handleType = (t) => setForm((f) => ({
    ...f,
    instance_type: t,
    base_url: t === 'cloud' ? CLOUD_DEFAULT : SELF_DEFAULT,
  }));

  const submit = async () => {
    if (dialog.mode === 'create') {
      if (!String(form.location_name || '').trim()) {
        toast.error('Location name is required');
        return;
      }
      if (form.transport === 'rest') {
        const missing = ['base_url', 'tenant_id', 'username', 'password']
          .filter((k) => !String(form[k] || '').trim());
        if (missing.length) { toast.error(`Missing: ${missing.join(', ')}`); return; }
      }
    }
    setSaving(true);
    try {
      if (dialog.mode === 'create') {
        const r = await api.createLocation(token, form);
        toast.success(`Location "${r.data.location_name}" created — click Test connection`);
      } else {
        // Only send password/app_token when the user actually typed something
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        if (!payload.app_token) delete payload.app_token;
        await api.updateLocation(token, dialog.location.location_id, payload);
        toast.success('Location updated');
      }
      setDialog({ open: false, mode: 'create', location: null });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const test = async (loc) => {
    setBusyId(loc.location_id);
    try {
      const r = await api.testLocation(token, loc.location_id);
      toast.success(`Connected to ${r.data.identifier}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Connection test failed');
      await load();
    } finally { setBusyId(null); }
  };

  const setDefault = async (loc) => {
    setBusyId(loc.location_id);
    try {
      await api.setDefaultLocation(token, loc.location_id);
      toast.success(`"${loc.location_name}" is now the default`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not set default');
    } finally { setBusyId(null); }
  };

  const doDelete = async (mode) => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await api.deleteLocation(token, confirmDel.location_id, mode);
      toast.success(mode === 'archive'
        ? `"${confirmDel.location_name}" archived — historical records kept`
        : `"${confirmDel.location_name}" and all its records deleted`);
      // If we deleted the currently-selected location, reset to All
      if (currentLocationId === confirmDel.location_id) setCurrentLocationId(null);
      setConfirmDel(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setDeleting(false); }
  };

  const saveBinding = async (locationId, patch) => {
    setBindingBusy(locationId);
    try {
      const current = destinations.bindings.find(b => b.location_id === locationId) || {};
      const next = {
        qbo_profile_id: patch.qbo_profile_id ?? current.qbo_profile_id ?? null,
        odoo_profile_id: patch.odoo_profile_id ?? current.odoo_profile_id ?? null,
      };
      await api.saveLocationBinding(token, locationId, next);
      toast.success('Destination updated for this location');
      const d = await api.listDestinations(token);
      setDestinations(d.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save binding');
    } finally { setBindingBusy(null); }
  };

  const cloud = form.instance_type === 'cloud';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="locations-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
            <MapPin className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">Locations</h1>
            <p className="text-slate-400 mt-1 max-w-2xl">
              Each venue you run needs its own UnTill instance. Add one per location, then use the switcher in the top bar to view that location&apos;s data — or view them combined with &ldquo;All locations&rdquo;.
            </p>
          </div>
        </div>
        <Button onClick={openCreate} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="add-location-btn">
          <Plus className="w-4 h-4 mr-2" /> Add location
        </Button>
      </div>

      {loading && !locations.length ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-500 mx-auto" />
          </CardContent>
        </Card>
      ) : locations.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800" data-testid="no-locations-empty">
          <CardContent className="p-12 text-center text-slate-400 space-y-3">
            <MapPin className="w-10 h-10 opacity-40 mx-auto" />
            <div className="text-white font-medium">No locations yet</div>
            <p className="max-w-md mx-auto text-sm">
              Add your first UnTill venue below. If you run one restaurant, that&apos;s all you need; if you have multiple, add each one and toggle between them from the header.
            </p>
            <Button onClick={openCreate} className="bg-blue-500 hover:bg-blue-600 text-white mt-2">
              <Plus className="w-4 h-4 mr-2" /> Add your first location
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {locations.map((loc) => (
            <Card key={loc.location_id} className="bg-slate-900/50 border-slate-800" data-testid={`location-card-${loc.location_id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-blue-300" />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-white text-base font-heading truncate">
                        {loc.location_name}
                        <span className="ml-2 align-middle text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-700 text-slate-400"
                          data-testid={`location-currency-${loc.location_id}`}>{loc.currency || DEFAULT_CURRENCY}</span>
                      </CardTitle>
                      <CardDescription className="text-slate-500 text-xs mt-0.5">
                        {loc.transport === 'webhook' ? (
                          <><Webhook className="w-3 h-3 inline mr-1" /> Webhook receiver</>
                        ) : loc.transport === 'csv' ? (
                          <><FileUp className="w-3 h-3 inline mr-1" /> CSV upload</>
                        ) : loc.transport === 'manual' ? (
                          <><Hand className="w-3 h-3 inline mr-1" /> Manual entry</>
                        ) : loc.instance_type === 'cloud' ? (
                          <><Cloud className="w-3 h-3 inline mr-1" /> Cloud · <span className="font-mono">{loc.tenant_id}</span></>
                        ) : (
                          <><Server className="w-3 h-3 inline mr-1" /> Self-hosted · <span className="font-mono">{loc.tenant_id}</span></>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 items-end flex-shrink-0">
                    {loc.is_default && (
                      <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 gap-1">
                        <Star className="w-3 h-3" /> Default
                      </Badge>
                    )}
                    {loc.verified ? (
                      <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Verified
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-800 text-slate-400 border-slate-700 gap-1">
                        <AlertCircle className="w-3 h-3" /> Unverified
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <Metric label="Products" value={loc.counts?.products ?? 0} />
                  <Metric label="Sales" value={loc.counts?.sales ?? 0} />
                  <Metric label="Customers" value={loc.counts?.customers ?? 0} />
                  <Metric label="Inventory" value={loc.counts?.inventory ?? 0} />
                </div>
                <LocationDestinations
                  location={loc}
                  destinations={destinations}
                  busy={bindingBusy === loc.location_id}
                  onSave={(patch) => saveBinding(loc.location_id, patch)}
                />
                {loc.transport === 'webhook' && (
                  <WebhookPanel location={loc} token={token} />
                )}
                {loc.last_verify_error && !loc.verified && (
                  <div className="rounded-md border border-rose-500/30 bg-rose-500/10 p-2 text-[11px] text-rose-200">
                    <strong className="text-rose-300">Last error:</strong> {loc.last_verify_error}
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <Button
                    size="sm" variant="outline"
                    className="h-8 border-slate-700 text-slate-200 hover:text-white hover:border-blue-500/60"
                    disabled={busyId === loc.location_id}
                    onClick={() => test(loc)}
                    data-testid={`test-${loc.location_id}`}
                  >
                    {busyId === loc.location_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Plug className="w-3.5 h-3.5 mr-1.5" />}
                    Test
                  </Button>
                  {!loc.is_default && (
                    <Button
                      size="sm" variant="outline"
                      className="h-8 border-slate-700 text-amber-300 hover:text-white hover:border-amber-500/60"
                      disabled={busyId === loc.location_id}
                      onClick={() => setDefault(loc)}
                      data-testid={`default-${loc.location_id}`}
                    >
                      <Star className="w-3.5 h-3.5 mr-1.5" /> Make default
                    </Button>
                  )}
                  <Button
                    size="sm" variant="outline"
                    className="h-8 border-slate-700 text-slate-200 hover:text-white"
                    onClick={() => openEdit(loc)}
                    data-testid={`edit-${loc.location_id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    className="h-8 border-rose-500/40 text-rose-300 hover:text-white hover:bg-rose-500/10 ml-auto"
                    onClick={() => setConfirmDel(loc)}
                    data-testid={`delete-${loc.location_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">
              {dialog.mode === 'create' ? 'Add a new location' : `Edit "${dialog.location?.location_name}"`}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {dialog.mode === 'create'
                ? 'Enter the UnTill connection details for this venue. You can test the connection right after saving.'
                : 'Update this venue\u2019s connection details. Leave password / AppToken blank to keep the ones you already saved.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-slate-300">Location name</Label>
                <Input value={form.location_name}
                  onChange={(e) => setForm({ ...form, location_name: e.target.value })}
                  placeholder="e.g. Downtown Store — 123 Main St"
                  className="bg-slate-950 border-slate-800 text-white mt-1.5"
                  data-testid="location-name" />
              </div>
              <div>
                <Label className="text-slate-300">Currency</Label>
                <CurrencyPicker value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">How does data reach SyncPoint?</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1.5">
                {TRANSPORTS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    disabled={dialog.mode === 'edit'}
                    onClick={() => setForm({ ...form, transport: t.id })}
                    data-testid={`transport-${t.id}`}
                    className={`p-2.5 rounded-lg border text-left transition-all text-xs ${
                      form.transport === t.id
                        ? 'border-blue-500/70 bg-blue-500/10 text-white'
                        : 'border-slate-800 hover:border-slate-700 bg-slate-900/40 text-slate-300'
                    } ${dialog.mode === 'edit' ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <div className="font-semibold mb-0.5">{t.label}</div>
                    <div className="text-[10px] text-slate-400">{t.hint}</div>
                  </button>
                ))}
              </div>
              {dialog.mode === 'edit' && (
                <p className="text-[10px] text-slate-500 mt-1.5">Transport is locked after creation — remove and re-add the location to change it.</p>
              )}
            </div>

            {form.transport === 'rest' ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <button type="button" onClick={() => handleType('cloud')} data-testid="type-cloud"
                    className={`p-3 rounded-lg border text-left transition-all text-sm ${cloud ? 'border-blue-500/60 bg-blue-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900/40'}`}>
                    <div className="flex items-center gap-2 mb-0.5"><Cloud className="w-4 h-4 text-blue-400" /><span className="text-white font-medium">Cloud (unTill Online)</span></div>
                    <p className="text-xs text-slate-400">Browser access at <span className="font-mono">*.untill.com</span></p>
                  </button>
                  <button type="button" onClick={() => handleType('self_hosted')} data-testid="type-self"
                    className={`p-3 rounded-lg border text-left transition-all text-sm ${!cloud ? 'border-blue-500/60 bg-blue-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900/40'}`}>
                    <div className="flex items-center gap-2 mb-0.5"><Server className="w-4 h-4 text-blue-400" /><span className="text-white font-medium">Self-hosted</span></div>
                    <p className="text-xs text-slate-400">Runs on a server at the venue</p>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">API base URL</Label>
                    <Input value={form.base_url}
                      onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                      className="bg-slate-950 border-slate-800 text-white mt-1.5 font-mono text-sm"
                      data-testid="location-base-url" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Tenant / database</Label>
                    <Input value={form.tenant_id}
                      onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
                      className="bg-slate-950 border-slate-800 text-white mt-1.5 font-mono text-sm"
                      data-testid="location-tenant" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Username</Label>
                    <Input value={form.username}
                      onChange={(e) => setForm({ ...form, username: e.target.value })}
                      className="bg-slate-950 border-slate-800 text-white mt-1.5"
                      data-testid="location-username" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Password{dialog.mode === 'edit' ? ' (leave blank to keep)' : ''}</Label>
                    <Input type="password" value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="bg-slate-950 border-slate-800 text-white mt-1.5"
                      data-testid="location-password" />
                  </div>
                  <div>
                    <Label className="text-slate-300">AppToken <span className="text-slate-500 text-xs">(optional — leave blank for demo/testing)</span></Label>
                    <Input type="password" value={form.app_token}
                      onChange={(e) => setForm({ ...form, app_token: e.target.value })}
                      className="bg-slate-950 border-slate-800 text-white mt-1.5 font-mono text-sm"
                      data-testid="location-app-token" />
                  </div>
                  <div>
                    <Label className="text-slate-300">AppName</Label>
                    <Input value={form.app_name}
                      onChange={(e) => setForm({ ...form, app_name: e.target.value })}
                      className="bg-slate-950 border-slate-800 text-white mt-1.5"
                      data-testid="location-app-name" />
                  </div>
                </div>
                <SoapBillFields form={form} setForm={setForm} />
              </>
            ) : (
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-sm text-slate-300">
                {form.transport === 'csv' && (
                  <>
                    <div className="font-medium text-white mb-1">CSV / XLSX file uploads</div>
                    <p className="text-xs text-slate-400">
                      Once this location is saved, drop your daily <span className="font-mono">sales</span>,
                      <span className="font-mono"> products</span>, or <span className="font-mono"> customers</span> file
                      directly on the location card. SyncPoint will parse and merge every row into this location.
                    </p>
                  </>
                )}
                {form.transport === 'webhook' && (
                  <>
                    <div className="font-medium text-white mb-1">Webhook endpoint</div>
                    <p className="text-xs text-slate-400">
                      SyncPoint will generate a signed URL and secret. Paste both into your POS or middleware and it can
                      push a JSON event per sale. The signed URL appears on the location card after you save.
                    </p>
                  </>
                )}
                {form.transport === 'manual' && (
                  <>
                    <div className="font-medium text-white mb-1">Manual entry</div>
                    <p className="text-xs text-slate-400">
                      No integration needed — the operator will add sales, products, and customers directly from the
                      SyncPoint dashboard. Great for pilot venues.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialog({ ...dialog, open: false })} className="border-slate-700 text-slate-200">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="save-location-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wifi className="w-4 h-4 mr-2" />}
              {dialog.mode === 'create' ? 'Create location' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 sm:max-w-md" data-testid="delete-location-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Remove &ldquo;{confirmDel?.location_name}&rdquo;?</DialogTitle>
            <DialogDescription className="text-slate-400">Pick how you want to handle this location&apos;s history.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
              <div className="flex items-center gap-2 text-amber-200 font-medium text-sm mb-1"><Archive className="w-4 h-4" /> Archive (recommended)</div>
              <p className="text-xs text-slate-400">Hide this location from the switcher and stop future syncs. Every historical product, sale, and journal entry stays queryable so your books close cleanly.</p>
            </div>
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3">
              <div className="flex items-center gap-2 text-rose-200 font-medium text-sm mb-1"><Trash2 className="w-4 h-4" /> Delete permanently</div>
              <p className="text-xs text-slate-400">Purge the location and <strong>every row</strong> tagged to it (products, sales, customers, inventory, journal entries, logs). This cannot be undone.</p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="ghost" onClick={() => setConfirmDel(null)} className="text-slate-400 hover:text-white">Cancel</Button>
            <div className="flex gap-2">
              <Button onClick={() => doDelete('archive')} disabled={deleting}
                className="bg-amber-500/90 hover:bg-amber-500 text-slate-950" data-testid="confirm-archive-btn">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
                Archive
              </Button>
              <Button onClick={() => doDelete('delete')} disabled={deleting}
                className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="confirm-delete-btn">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-md bg-slate-950/60 border border-slate-800 p-2 text-center">
      <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg font-mono text-white mt-0.5">{value}</div>
    </div>
  );
}

function WebhookPanel({ location, token }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.getLocationWebhook(token, location.location_id);
      setInfo(r.data);
    } catch (e) { /* non-fatal */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [location.location_id]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch (e) {
      toast.error(`Could not copy — ${e.message}`);
    }
  };

  const rotate = async () => {
    if (!window.confirm('Rotate the webhook secret? Any POS currently using the old secret will get 401 until updated.')) return;
    setRotating(true);
    try {
      const r = await api.rotateLocationWebhook(token, location.location_id);
      toast.success('Secret rotated — update your POS with the new value');
      setInfo((v) => ({ ...(v || {}), secret: r.data.secret }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotation failed');
    } finally { setRotating(false); }
  };

  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/40 p-2.5 space-y-2" data-testid={`webhook-panel-${location.location_id}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium flex items-center gap-1.5">
          <Webhook className="w-3 h-3 text-emerald-300" /> Webhook credentials
        </div>
        {info?.last_received_at && (
          <span className="text-[10px] text-emerald-300/80">
            Last event: {new Date(info.last_received_at).toLocaleString()}
          </span>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      ) : info ? (
        <div className="space-y-1.5">
          <CredRow label="URL" value={info.url} testId={`webhook-url-${location.location_id}`} onCopy={() => copy(info.url, 'Webhook URL')} />
          <CredRow label="Header" value={`X-SyncPoint-Secret: ${info.secret}`} testId={`webhook-secret-${location.location_id}`} onCopy={() => copy(info.secret, 'Secret')} mono />
          <div className="pt-1">
            <Button
              size="sm" variant="outline"
              onClick={rotate} disabled={rotating}
              className="h-7 text-[11px] border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:text-white"
              data-testid={`rotate-webhook-${location.location_id}`}
            >
              {rotating ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <RefreshCw className="w-3 h-3 mr-1.5" />}
              Rotate secret
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-xs text-rose-300">Could not load webhook info</div>
      )}
    </div>
  );
}

function CredRow({ label, value, testId, onCopy, mono }) {
  return (
    <div className="flex items-stretch gap-1.5">
      <div className="flex-1 min-w-0 rounded-md bg-slate-900/70 border border-slate-800 px-2 py-1.5">
        <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
        <div
          className={`text-[11px] text-slate-200 truncate ${mono ? 'font-mono' : ''}`}
          data-testid={testId}
          title={value}
        >
          {value}
        </div>
      </div>
      <button
        type="button"
        onClick={onCopy}
        className="rounded-md border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 hover:bg-slate-800 px-2"
        aria-label={`Copy ${label}`}
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function LocationDestinations({ location, destinations, busy, onSave }) {
  const binding = (destinations.bindings || []).find(b => b.location_id === location.location_id) || {};
  const qboProfiles = destinations.qbo_profiles || [];
  const odooProfiles = destinations.odoo_profiles || [];
  const selectedQbo = binding.qbo_profile_id || 'primary';
  const selectedOdoo = binding.odoo_profile_id || 'primary';

  const findProfile = (list, id) => list.find(p => p.profile_id === id);
  const qboProf = findProfile(qboProfiles, selectedQbo);
  const odooProf = findProfile(odooProfiles, selectedOdoo);

  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/40 p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
          Destinations for this location
        </div>
        {busy && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <BindingSelect
          icon={<Building2 className="w-3.5 h-3.5 text-emerald-300" />}
          label="QuickBooks"
          value={selectedQbo}
          profiles={qboProfiles}
          connected={qboProf?.connected}
          testId={`bind-qbo-${location.location_id}`}
          disabled={busy}
          onChange={(pid) => onSave({ qbo_profile_id: pid })}
        />
        <BindingSelect
          icon={<Database className="w-3.5 h-3.5 text-purple-300" />}
          label="Odoo"
          value={selectedOdoo}
          profiles={odooProfiles}
          connected={odooProf?.connected}
          testId={`bind-odoo-${location.location_id}`}
          disabled={busy}
          onChange={(pid) => onSave({ odoo_profile_id: pid })}
        />
      </div>
    </div>
  );
}

function BindingSelect({ icon, label, value, profiles, connected, testId, disabled, onChange }) {
  const empty = !profiles || profiles.length === 0;
  return (
    <div>
      <div className="flex items-center gap-1 mb-1 text-[10px] text-slate-400 uppercase tracking-wide">
        {icon} <span>{label}</span>
        {connected ? (
          <CheckCircle2 className="w-3 h-3 text-emerald-400" />
        ) : (
          <AlertCircle className="w-3 h-3 text-slate-500" />
        )}
      </div>
      {empty ? (
        <Link to="/destinations" className="text-xs text-blue-300 hover:text-blue-200 underline">
          Add a {label} destination →
        </Link>
      ) : (
        <select
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full h-8 rounded-md bg-slate-950 border border-slate-800 text-slate-100 text-xs px-2 focus:border-blue-500 focus:outline-none"
        >
          {profiles.map(p => (
            <option key={p.profile_id} value={p.profile_id}>
              {p.profile_name}{p.is_default ? ' (Primary)' : ''}{p.connected ? ' • connected' : ''}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
